import React, { ReactNode } from "react";
import parse, { DOMNode } from 'html-react-parser';
import { markdown } from '../../../common/markdown';
import { AllieTextWrapperMessage } from '../ChatResponse/ChatResponse.styles';
import { AllieColors } from "../../../common/color.types";
import { TypewriterText } from '../ChatResponse/components/TypewriterText';
import { CitationProvider, Citation, CitationMetadata, CitationLabels } from '../Citation';
import { CitationModal, CitationPreviewProps, CitationModalProps } from '../CitationModal';
import { useCitation } from '../Citation/CitationContext';
import { replaceMarkdownTable } from '../MarkdownText/markdownTableReplace';

interface FactoryGptTextResponseProps {
  message: string;
  textColor?: AllieColors;
  animateResponse?: boolean;
  maxDuration?: number;
  maxSpeed?: number;
  onRenderComplete?: () => void;
  citations?: CitationMetadata[];
  citationLabels?: CitationLabels;
  renderCitationPreview?: (props: CitationPreviewProps) => ReactNode;
  renderCitationModal?: (props: CitationModalProps) => ReactNode;
  onDownloadCsv?: (csvData: string) => void;
  downloadCsvTooltip?: string;
  onDownloadXlsx?: (csvData: string) => void;
}

const FactoryGptTextResponseContent: React.FC<Omit<
  FactoryGptTextResponseProps,
  'citations' | 'citationLabels'
>> = ({
  message,
  textColor = 'neutral-60',
  animateResponse = false,
  maxDuration = 3000,
  maxSpeed = 100,
  onRenderComplete,
  renderCitationPreview,
  renderCitationModal,
  onDownloadCsv,
  downloadCsvTooltip,
  onDownloadXlsx,
}) => {
    const { isModalOpen, selectedCitation, closeCitation, labels } = useCitation();

    const parseWithCitations = (text: string) => {
      if (!text || typeof text !== 'string') {
        return '';
      }

      // Step 1: Extract citation spans and their content before markdown parsing
      const citationSpans: { id: string; content: string; placeholder: string }[] = [];
      let processedText = text;

      // Regex to match <span data-cite="...">content</span>
      const spanRegex = /<span\s+data-cite="([^"]+)">([\s\S]*?)<\/span>/g;
      let match;
      let index = 0;

      while ((match = spanRegex.exec(text)) !== null) {
        const citationId = match[1];
        const content = match[2];
        // Use a placeholder that won't be interpreted by markdown
        const placeholder = `{{CITATION_${index}}}`;

        citationSpans.push({
          id: citationId,
          content: content,
          placeholder: placeholder,
        });

        index++;
      }

      // Replace spans with placeholders
      processedText = text.replace(spanRegex, (_, citationId, content) => {
        const idx = citationSpans.findIndex(
          span => span.id === citationId && span.content === content
        );
        return citationSpans[idx]?.placeholder || '';
      });

      // Step 2: Convert markdown to HTML (placeholders remain intact)
      const htmlString = markdown.parse(processedText) as string;
      // Step 3: Parse HTML and replace placeholders with Citation components
      const replacePlaceholders = (node: DOMNode) => {
        // Check if this is a text node containing a placeholder
        if (node.type === 'text' && node.data) {
          const placeholderRegex = /\{\{CITATION_\d+\}\}/;

          if (placeholderRegex.test(node.data)) {
            const parts: (string | React.ReactElement)[] = [];
            const segments = node.data.split(/(\{\{CITATION_\d+\}\})/);

            segments.forEach((segment, idx) => {
              const segmentMatch = segment.match(/^\{\{CITATION_(\d+)\}\}$/);
              if (segmentMatch) {
                // This is a placeholder
                const citationIndex = parseInt(segmentMatch[1], 10);
                const citation = citationSpans[citationIndex];

                if (citation) {
                  // Parse the citation content as inline markdown (no block elements)
                  const citationHtml = markdown.parseInline(citation.content, {
                    breaks: false,
                  }) as string;

                  // Parse the citation HTML to React elements
                  const citationContent = parse(citationHtml);

                  parts.push(
                    <React.Fragment key={`citation-${citationIndex}-${idx}`}>
                      {citationContent}
                      {' '}
                      <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                        <Citation citationId={citation.id} />
                      </span>
                    </React.Fragment>
                  );
                }
              } else if (segment) {
                // This is regular text
                parts.push(segment);
              }
            });

            return <>{parts}</> as any;
          }
        }
        return undefined;
      };

      const options: Parameters<typeof parse>[1] = {
        replace: (domNode) => {
          const placeholderResult = replacePlaceholders(domNode);
          if (placeholderResult !== undefined) return placeholderResult;

          return replaceMarkdownTable(domNode, options, {
            onDownloadCsv,
            downloadCsvTooltip,
            onDownloadXlsx,
          });
        },
      };

      return parse(htmlString, options);
    };

    if (animateResponse) {
      return (
        <>
          <AllieTextWrapperMessage type="body1" color={textColor}>
            <TypewriterText
              message={message}
              textColor={textColor}
              animateResponse={animateResponse}
              maxDuration={maxDuration}
              maxSpeed={maxSpeed}
              onRenderComplete={onRenderComplete}
              parseFunction={parseWithCitations}
            />
          </AllieTextWrapperMessage>
          {isModalOpen && selectedCitation && (
            renderCitationModal ? renderCitationModal({
              isOpen: isModalOpen,
              citation: selectedCitation,
              onClose: closeCitation,
              labels: labels,
              renderPreview: renderCitationPreview,
            }) : (
              <CitationModal
                isOpen={isModalOpen}
                citation={selectedCitation}
                onClose={closeCitation}
                labels={labels}
                renderPreview={renderCitationPreview}
              />
            )
          )}
        </>
      );
    }

    return (
      <>
        <AllieTextWrapperMessage type="body1" color={textColor}>
          {message ? parseWithCitations(message) : ''}
        </AllieTextWrapperMessage>
        {isModalOpen && selectedCitation && (
          renderCitationModal ? renderCitationModal({
            isOpen: isModalOpen,
            citation: selectedCitation,
            onClose: closeCitation,
            labels: labels,
            renderPreview: renderCitationPreview,
          }) : (
            <CitationModal
              isOpen={isModalOpen}
              citation={selectedCitation}
              onClose={closeCitation}
              labels={labels}
              renderPreview={renderCitationPreview}
            />
          )
        )}
      </>
    );
  }

const parseTextWithTableDownload = (
  message: string,
  onDownloadCsv?: (csvData: string) => void,
  downloadCsvTooltip?: string,
  onDownloadXlsx?: (csvData: string) => void,
) => {
  if (!message || typeof message !== 'string') return '';

  const htmlString = markdown.parse(message) as string;

  const options: Parameters<typeof parse>[1] = {
    replace: (domNode) =>
      replaceMarkdownTable(domNode, options, {
        onDownloadCsv,
        downloadCsvTooltip,
        onDownloadXlsx,
      }),
  };

  return parse(htmlString, options);
};

export const FactoryGptTextResponse: React.FC<FactoryGptTextResponseProps> = ({
  citations = [],
  citationLabels,
  renderCitationPreview,
  renderCitationModal,
  ...props
}) => {
  if (citations.length === 0 && !props.animateResponse) {
    return (
      <AllieTextWrapperMessage type="body1" color={props.textColor || 'neutral-60'}>
        {props.message
          ? parseTextWithTableDownload(
              props.message,
              props.onDownloadCsv,
              props.downloadCsvTooltip,
              props.onDownloadXlsx,
            )
          : ''}
      </AllieTextWrapperMessage>
    );
  }

  return (
    <CitationProvider citations={citations} labels={citationLabels}>
      <FactoryGptTextResponseContent {...props}
        renderCitationPreview={renderCitationPreview} renderCitationModal={renderCitationModal} />
    </CitationProvider>
  );
};